// components/UserSigninForm.tsx

import { useEffect, useReducer, useState } from 'react';

import ErrorMessage from '@/components/common/ErrorMessage';
import MultiStepFormStepper from '@/components/common/multi-step-form/MultiStepFormStepper';
import { StepItem, useStepper } from '@/core/hooks/useStepper';
import { BasicResponse } from '@/core/models/common-types';

import { userSigninReducer, initialUserSigninState, UserSigninState } from '../states/userSigninReducer';
import UserSigninCredentialsStep from '../steps/UserSigninCredentialsStep';
import UserSigninRequestOtpStep from '../steps/UserSigninRequestOtpStep';
import UserSigninVerifyOtpStep from '../steps/UserSigninVerifyOtpStep';

import { MessageBox } from '@/core/utils/message-box';

type Step = 'credentials' | 'request-otp' | 'verify-otp';

export const updateStep = (step: Step) => {
  const url = new URL(window.location.href);
  url.searchParams.set('step', step);
  window.history.replaceState({}, '', url);
};

interface Props {
  loginSessionId?: string;
  initialMessage?: BasicResponse | null;
}

const UserSigninForm: React.FC<Props> = ({ loginSessionId, initialMessage }) => {
  const [error] = useState<BasicResponse | null>(initialMessage && !initialMessage.status ? initialMessage : null);
 

  const steps: StepItem<Step>[] = [
    { key: 'credentials', label: 'Sign In' },
    { key: 'request-otp', label: 'Verify Method' },
    { key: 'verify-otp', label: 'Verify OTP' },
  ];

  const { labels, currentKey, currentIndex, next, prev } = useStepper<Step>(steps, {
    initial: 'credentials',
    visibilityDeps: [],
  });

  const [state, dispatch] = useReducer(userSigninReducer, initialUserSigninState);
  useEffect(() => {
    if (initialMessage?.status) {
      MessageBox.toastSuccess(initialMessage.info ?? 'Success.');
    }
  }, []);
  useEffect(() => {
    if (!loginSessionId) return;

    dispatch({
      type: 'UPDATE',
      payload: {
        loginSessionId: loginSessionId,
      },
    });
  }, [loginSessionId]);




  const handleNext = (values: Partial<UserSigninState>) => {
    dispatch({
      type: 'UPDATE',
      payload: values,
    });

    next();

    // updateStep(...)
  };

  const handleSubmit = (values: Partial<UserSigninState>) => {
    dispatch({
      type: 'UPDATE',
      payload: values,
    });

    // TODO:
    // Verify OTP API
    // Complete MFA Sign In
    // Redirect user

    console.log('Submitting signin:', {
      ...state,
      ...values,
    });
  };

 

  return (
    <div className="container py-4">
      <div className="row justify-content-center">
        <div className="col-xl-6 col-lg-7 col-md-9">
          <div className="card border-0 shadow-sm">
            <div className="card-body p-4 p-md-5">
              <div className="text-center mb-4">
                <img src="/images/logo.png" alt="Logo" className="img-fluid mb-3" style={{ maxHeight: 100 }} />

                <h2 className="fw-bold mb-2">Sign In</h2>

                <p className="text-muted mb-0">Sign in securely using multi-factor authentication.</p>
              </div>

              <div className="mb-4">
                <MultiStepFormStepper steps={labels} currentStep={currentIndex} showSteps={false} />
              </div>

              <ErrorMessage error={error} />

              <div className="mt-4">
                {currentKey === 'credentials' && (
                  <UserSigninCredentialsStep state={state} dispatch={dispatch} onNext={handleNext} />
                )}

                {currentKey === 'request-otp' && (
                  <UserSigninRequestOtpStep state={state} dispatch={dispatch} onNext={handleNext} onPrevious={prev} />
                )}

                {currentKey === 'verify-otp' && (
                  <UserSigninVerifyOtpStep
                    state={state}
                    dispatch={dispatch}
                    onPrevious={prev}
                    onSubmit={handleSubmit}
                  />
                )}
              </div>
<div className="mt-4">
  <div className="d-flex align-items-center gap-3 mb-4">
    <div className="border-top flex-grow-1" />
    <span className="text-muted small fw-semibold text-uppercase">or</span>
    <div className="border-top flex-grow-1" />
  </div>

<a
  href="/auth/ecitizen"
  className="btn btn-outline-primary w-100 py-2 fw-semibold rounded-3"
>
  Continue with eCitizen
</a>
  <p className="text-center text-muted mt-4 mb-0">
    Don&apos;t have an account?{" "}
    <a href="/register" className="fw-semibold text-primary text-decoration-none">
      Create account
    </a>
  </p>
</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserSigninForm;
